import _ from 'lodash'
import dayjs from 'dayjs'

// {
//     time: '08:00',
//     scheduled: {
//         due: 3,
//         signed: 2,
//         notAdministered: 1,
//     },
//     PRN: 1
// },

/**
@description Gets the latest round time. E.g. if the
current time is 12:30, and the round times are 08:00,
12:00, 16:00 and 20:00, the function will return 12:00.

@param {array} rounds. The daily rounds. Example round:
    {
        time: '08:00',
        scheduled: {
            due: 3,
            signed: 2,
            notAdministered: 1,
        },
        PRN: 1
    }

@param {object} currentDate. A dayjs object of the current
date. Defaults to dayjs(), but can be set to any date.

@returns {string} Latest round time e.g. 06:00
*/

export const getLatestRoundTime = (props) => {
  const { currentDate = dayjs(), rounds } = props

  const pastRounds = _.filter(rounds, (round) => {
    return round.time < currentDate.format('HH:mm')
  })

  if (!pastRounds.length) return 'NA'

  const sortedPastRounds = _.sortBy(pastRounds, (round) =>
    dayjs(round.time, 'HH:mm')
  )

  const latestRoundTime = _.last(sortedPastRounds).time

  return latestRoundTime
}

/**
@description Determines whether a round is overdue.

@param {object} round. The round to be tested. Example round:
    {
        time: '08:00',
        scheduled: {
            due: 3,
            signed: 2,
            notAdministered: 1,
        },
        PRN: 1
    }

@param {string} latestRoundTime. The most recent round time.
E.g. 12:00

@param {object} currentDate. A dayjs object of the current
date. Defaults to dayjs(), but can be set to any date.

@param {object} today. A dayjs object of today's date. Used
for testing.

@returns {bool} False if round is most recent or in the future.
True otherwise.
*/

export const isRoundOverdue = ({
  round,
  latestRoundTime,
  currentDate = dayjs(),
  today = dayjs(),
}) => {
  if (!currentDate.isSame(today, 'day'))
    return currentDate.isBefore(today, 'day')

  return !(
    round.time > currentDate.format('HH:mm') || round.time === latestRoundTime
  )
}

/**
@description Get the daily totals for all the rounds in the day.

@param {array} rounds. The daily rounds. Example round:
    {
        time: '08:00',
        scheduled: {
            due: 3,
            signed: 2,
            notAdministered: 1,
        },
        PRN: 1
    }

@param {string} latestRoundTime. The most recent round time.
E.g. 12:00

@param {object} currentDate. A dayjs object of the current
date. Defaults to dayjs(), but can be set to any date.

@returns {object} dailyTotals object. Contains all daily totals, e.g.
    {
        signed: 10,
        due: 5,
        notSigned: 4,
        notAdministered: 3,
        PRN: 1
    }
*/

export const getDailyTotals = (props) => {
  const { currentDate = dayjs(), rounds, latestRoundTime } = props

  let signed = 0
  let due = 0
  let notSigned = 0
  let notAdministered = 0
  let PRN = 0

  _.forEach(rounds, (round) => {
    const isOverdue = isRoundOverdue({ round, latestRoundTime, currentDate })

    isOverdue
      ? (notSigned += round.scheduled?.due)
      : (due += round.scheduled?.due)

    signed += round.scheduled?.signed
    notAdministered += round.scheduled?.notAdministered
    PRN += round.PRN
  })

  return {
    signed,
    due,
    notSigned,
    notAdministered,
    PRN,
  }
}
